import { useState } from "react";
import { toast } from "react-toastify";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import type { PayrollRun } from "features/Payroll/types/payroll.types";
import { deletePayrollRun } from "features/Payroll/services/PayrollService";

type Props = {
  run: PayrollRun | null;
  isOpen: boolean;
  onClose: () => void;
  onDeleted?: () => void | Promise<void>;
};

const DeletePayrollRunConfirmModal = ({ run, isOpen, onClose, onDeleted }: Props) => {
  const [busy, setBusy] = useState(false);

  const handleClose = () => {
    if (busy) return;
    onClose();
  };

  const handleConfirm = async () => {
    if (!run) return;
    setBusy(true);
    try {
      const res = await deletePayrollRun(run.id);
      if (res.success) {
        onClose();
        toast.success("Payroll run deleted");
        await onDeleted?.();
      } else {
        toast.error(res.message || "Could not delete payroll run");
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || "Could not delete payroll run");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal centered isOpen={isOpen && run != null} toggle={handleClose}>
      <ModalHeader toggle={handleClose}>Delete payroll run</ModalHeader>
      <ModalBody>
        <p className="text-muted mb-0">
          This will permanently delete payroll run{" "}
          <span className="fw-semibold">{run?.code ?? `#${run?.id}`}</span> and
          all its employee entries. This action cannot be undone.
        </p>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={handleClose} disabled={busy}>
          Keep run
        </Button>
        <Button color="danger" onClick={() => void handleConfirm()} disabled={busy}>
          {busy ? "Deleting…" : "Delete run"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default DeletePayrollRunConfirmModal;
